import { useEffect, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { IncidentSummary, summarizeIncidents } from './githubIncidents';

const useStyles = makeStyles(theme => ({
  headline: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  counter: {
    marginBottom: 0,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  ongoingBadge: {
    padding: theme.spacing(0.25, 1),
    backgroundColor: theme.palette.errorBackground,
    color: theme.palette.errorText,
    // Cast: MUI v4's FontWeightProperty predates the csstype union the theme now uses.
    fontWeight: theme.typography.fontWeightBold as number,
  },
}));

export const GithubIncidentSign = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [summary, setSummary] = useState<IncidentSummary>();
  const [error, setError] = useState<Error>();
  const classes = useStyles();

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('proxy');
        const response = await fetch(
          `${baseUrl}/githubstatus-proxy/history.rss`,
        );
        if (!response.ok) {
          throw new Error(
            `Failed to load GitHub incidents: ${response.status} ${response.statusText}`,
          );
        }
        const xml = await response.text();
        if (active) {
          setSummary(summarizeIncidents(xml, new Date()));
        }
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetch]);

  return (
    <InfoCard
      title={
        <span className={classes.title}>
          GitHub Status
          {summary?.lastIncident.ongoing && (
            <Typography
              variant="caption"
              component="span"
              className={classes.ongoingBadge}
            >
              Incident ongoing
            </Typography>
          )}
        </span>
      }
      variant="fullHeight"
    >
      {error && <ResponseErrorPanel error={error} />}
      {!error && !summary && <Progress />}
      {summary && (
        <>
          <div className={classes.headline}>
            <Typography
              variant="h1"
              component="span"
              color="textPrimary"
              className={classes.counter}
            >
              {summary.daysSince}
            </Typography>
            <Typography variant="h6" component="span" color="textPrimary">
              days without an incident
            </Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            {summary.lastIncident.ongoing
              ? 'Ongoing: '
              : `Record: ${summary.recordDays} days · Last: `}
            <Link to={summary.lastIncident.link}>
              {summary.lastIncident.title}
            </Link>
          </Typography>
        </>
      )}
    </InfoCard>
  );
};
